/**
 * The list detail page's filters over its companies (Trello #831), and its
 * page, held in the URL so a refresh, a shared link and the side panel's
 * `?company=` all keep them. The same write rules as `useCompanyListsFilters`
 * and People Lists' `useImportsListFilters`:
 *
 * - every write is `{ replace: true }`: a filter is a view of the page, not a
 *   place Back should step through;
 * - a filter change drops `page` IN THE SAME WRITE, so no render pairs the new
 *   filter with the old page;
 * - writes are functional (`setSearchParams(current => …)`), so `company`
 *   and any other param survive;
 * - `page` is read leniently: garbage or < 1 is 1.
 *
 * The text and number boxes are drafts, committed together after the same
 * 300 ms debounce People Lists' boxes use; the two selects commit at once.
 * When the URL changes under the drafts (Back/Forward, a link), the drafts
 * follow it, so the debounce never writes a stale box over the entry the
 * reader navigated to. The page that owns this hook is keyed by list id, so
 * a different list starts from its own URL, never from the last list's boxes.
 *
 * The sort lives here too (`sort` / `dir`, Trello #831 review M2), so a
 * refresh or Back shows the same rows in the same order; the default
 * (name, ascending) is written as no params, and a sort change drops `page`
 * in the same write.
 * The URL holds what the reader typed; the API gets only what parses (an
 * employee count is a whole number ≥ 0, revenue any number ≥ 0, in $M).
 *
 * Deviation from People Lists, on purpose: People Lists' detail page
 * (`ImportDetailPage`) keeps its filters in component state. Here they live
 * in the URL, as Trello #831 asks, with the firm page's URL rules.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { CompanyMemberSortKey, MemberFilterParams } from "../../clients/companyLists.client";
import type { MemberSort } from "./queries";

/** Same debounce as People Lists' filter boxes. */
export const MEMBER_FILTER_DEBOUNCE_MS = 300;

const PAGE_PARAM = "page";

/** The text/number boxes and their URL params. */
const TEXT_PARAMS = {
  q: "q",
  employeesMin: "employees_min",
  employeesMax: "employees_max",
  revenueMin: "revenue_min",
  revenueMax: "revenue_max",
} as const;
const WE_KNOW_PARAM = "we_know";
const LINKEDIN_PARAM = "linkedin";
const SORT_PARAM = "sort";
const DIR_PARAM = "dir";

const MEMBER_SORT_KEYS: readonly CompanyMemberSortKey[] = [
  "name",
  "hq_country",
  "sector",
  "founded_year",
  "employees",
  "revenue_usd_m",
  "added_at",
  "people_known",
];
const DEFAULT_SORT: MemberSort = { key: "name", dir: "asc" };

export type MemberTextField = keyof typeof TEXT_PARAMS;
export type MemberDrafts = Record<MemberTextField, string>;
export type WeKnowChoice = "any" | "yes" | "no";
export type LinkedinChoice = "any" | "matched" | "not_matched";

const TEXT_FIELDS = Object.keys(TEXT_PARAMS) as MemberTextField[];

export interface CompanyMembersFilters {
  /** What the boxes show (raw, un-debounced). */
  drafts: MemberDrafts;
  setDraft: (field: MemberTextField, value: string) => void;
  weKnow: WeKnowChoice;
  setWeKnow: (value: WeKnowChoice) => void;
  linkedin: LinkedinChoice;
  setLinkedin: (value: LinkedinChoice) => void;
  /** The committed filters as API params; absent keys filter nothing. */
  params: MemberFilterParams;
  /** Any filter is narrowing the list. */
  active: boolean;
  page: number;
  goToPage: (next: number) => void;
  /** Clear every filter and go back to page 1, in one write. */
  reset: () => void;
  sort: MemberSort;
  /** A new column starts ascending, the same one flips; back to page 1. */
  toggleSort: (key: CompanyMemberSortKey) => void;
}

function readPage(params: URLSearchParams): number {
  const parsed = Number.parseInt(params.get(PAGE_PARAM) ?? "1", 10);
  if (!Number.isSafeInteger(parsed)) return 1;
  return Math.max(1, parsed);
}

function readText(params: URLSearchParams, field: MemberTextField): string {
  return params.get(TEXT_PARAMS[field])?.trim() ?? "";
}

/** A bound as the API takes it, or undefined when blank or not a number. */
function parseBound(raw: string, integer: boolean): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return integer ? Math.floor(n) : n;
}

function readSort(params: URLSearchParams): MemberSort {
  const key = params.get(SORT_PARAM) as CompanyMemberSortKey | null;
  if (!key || !MEMBER_SORT_KEYS.includes(key)) return DEFAULT_SORT;
  return { key, dir: params.get(DIR_PARAM) === "desc" ? "desc" : "asc" };
}

function readWeKnow(params: URLSearchParams): WeKnowChoice {
  const v = params.get(WE_KNOW_PARAM);
  return v === "yes" || v === "no" ? v : "any";
}

function readLinkedin(params: URLSearchParams): LinkedinChoice {
  const v = params.get(LINKEDIN_PARAM);
  return v === "matched" || v === "not_matched" ? v : "any";
}

export function useCompanyMembersFilters(): CompanyMembersFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  // Seeded from the URL once; after that they are the reader's drafts.
  const [drafts, setDrafts] = useState<MemberDrafts>(() => {
    const out = {} as MemberDrafts;
    for (const f of TEXT_FIELDS) out[f] = readText(searchParams, f);
    return out;
  });

  // Keyed on the VALUES, not on `searchParams`: opening the panel
  // (`?company=`) or paging must not look like the boxes' params moved, or the
  // follow-the-URL effect below would wipe a box the reader is still typing in.
  const committedKey = TEXT_FIELDS.map((f) => readText(searchParams, f)).join("\u0000");
  const committed = useMemo(() => {
    const values = committedKey.split("\u0000");
    const out = {} as MemberDrafts;
    TEXT_FIELDS.forEach((f, i) => {
      out[f] = values[i] ?? "";
    });
    return out;
  }, [committedKey]);
  const weKnow = readWeKnow(searchParams);
  const linkedin = readLinkedin(searchParams);
  const page = readPage(searchParams);
  const sortKey = readSort(searchParams).key;
  const sortDir = readSort(searchParams).dir;
  const sort = useMemo<MemberSort>(() => ({ key: sortKey, dir: sortDir }), [sortKey, sortDir]);

  // Stable callbacks that still see the freshest setter (as in
  // `useImportsListFilters`, whose note has the measured reason).
  const latest = useRef({ setSearchParams });
  useLayoutEffect(() => {
    latest.current = { setSearchParams };
  });

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void, { keepPage = false } = {}) => {
      latest.current.setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutate(next);
          if (!keepPage) next.delete(PAGE_PARAM);
          return next;
        },
        { replace: true }
      );
    },
    []
  );

  // The URL moved under the drafts (Back/Forward, a link): follow it. Our own
  // debounced write lands equal to the trimmed draft and changes nothing.
  useEffect(() => {
    setDrafts((prev) => {
      const stale = TEXT_FIELDS.filter((f) => prev[f].trim() !== committed[f]);
      if (stale.length === 0) return prev;
      const next = { ...prev };
      for (const f of stale) next[f] = committed[f];
      return next;
    });
  }, [committed]);

  // Debounce the boxes into the URL, all of them in one write. The guard
  // compares trimmed with trimmed, so the mount run and a trailing space do
  // not write (and eat a pager click), as on People Lists.
  useEffect(() => {
    const id = setTimeout(() => {
      const changed = TEXT_FIELDS.filter((f) => drafts[f].trim() !== committed[f]);
      if (changed.length === 0) return;
      update((params) => {
        for (const f of changed) {
          const value = drafts[f].trim();
          if (value) params.set(TEXT_PARAMS[f], value);
          else params.delete(TEXT_PARAMS[f]);
        }
      });
    }, MEMBER_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [drafts, committed, update]);

  const setDraft = useCallback((field: MemberTextField, value: string) => {
    setDrafts((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setWeKnow = useCallback(
    (value: WeKnowChoice) =>
      update((params) => {
        if (value === "any") params.delete(WE_KNOW_PARAM);
        else params.set(WE_KNOW_PARAM, value);
      }),
    [update]
  );

  const setLinkedin = useCallback(
    (value: LinkedinChoice) =>
      update((params) => {
        if (value === "any") params.delete(LINKEDIN_PARAM);
        else params.set(LINKEDIN_PARAM, value);
      }),
    [update]
  );

  const goToPage = useCallback(
    (next: number) => {
      const target = Math.max(1, next);
      update(
        (params) => {
          if (target <= 1) params.delete(PAGE_PARAM);
          else params.set(PAGE_PARAM, String(target));
        },
        { keepPage: true }
      );
    },
    [update]
  );

  const reset = useCallback(() => {
    setDrafts({ q: "", employeesMin: "", employeesMax: "", revenueMin: "", revenueMax: "" });
    update((params) => {
      for (const f of TEXT_FIELDS) params.delete(TEXT_PARAMS[f]);
      params.delete(WE_KNOW_PARAM);
      params.delete(LINKEDIN_PARAM);
    });
  }, [update]);

  const toggleSort = useCallback(
    (key: CompanyMemberSortKey) =>
      update((params) => {
        const current = readSort(params);
        const next: MemberSort =
          current.key === key
            ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
            : { key, dir: "asc" };
        if (next.key === DEFAULT_SORT.key && next.dir === DEFAULT_SORT.dir) {
          params.delete(SORT_PARAM);
          params.delete(DIR_PARAM);
        } else {
          params.set(SORT_PARAM, next.key);
          params.set(DIR_PARAM, next.dir);
        }
      }),
    [update]
  );

  const params = useMemo<MemberFilterParams>(() => {
    const out: MemberFilterParams = {};
    if (committed.q) out.q = committed.q;
    const employeesMin = parseBound(committed.employeesMin, true);
    const employeesMax = parseBound(committed.employeesMax, true);
    const revenueMin = parseBound(committed.revenueMin, false);
    const revenueMax = parseBound(committed.revenueMax, false);
    if (employeesMin !== undefined) out.employees_min = employeesMin;
    if (employeesMax !== undefined) out.employees_max = employeesMax;
    if (revenueMin !== undefined) out.revenue_min = revenueMin;
    if (revenueMax !== undefined) out.revenue_max = revenueMax;
    if (weKnow !== "any") out.we_know = weKnow;
    if (linkedin !== "any") out.linkedin = linkedin;
    return out;
  }, [committed, weKnow, linkedin]);

  return {
    drafts,
    setDraft,
    weKnow,
    setWeKnow,
    linkedin,
    setLinkedin,
    params,
    active: Object.keys(params).length > 0,
    page,
    goToPage,
    reset,
    sort,
    toggleSort,
  };
}
